import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
// Bundle English inline so the first paint NEVER shows raw i18n keys
// (previously loaded via /public/locales/en/common.json fetch, which
// racy-lost against BusinessLanding's initial render on slow networks).
import enCommon from './locales/en-common.json';

// Locales we ship out of the box. Order matches the UI language selector.
// Hindi (hi) and Telugu (te) translations exist in /public/locales but are
// hidden from the UI switcher for now — enable by adding them back here
// once the copy has been reviewed by native speakers.
export const SUPPORTED_LANGS = [
  { code: 'en', label: 'English',    flag: '🇬🇧' },
  { code: 'pt', label: 'Português (Brasil)', flag: '🇧🇷' },
];

/**
 * Loader — pulls the JSON bundle from /public/locales/{lang}/common.json.
 * Only used for NON-English languages (English is bundled statically above).
 */
const backendLoader = async (lang) => {
  try {
    const r = await fetch(`/locales/${lang}/common.json`);
    if (!r.ok) throw new Error(`load failed ${lang}`);
    return await r.json();
  } catch {
    return null;
  }
};

async function preload(lang) {
  if (lang === 'en') return; // already bundled
  const data = await backendLoader(lang);
  if (data) {
    // Pass the whole file — top-level keys like `landing`, `nav`, etc are
    // path segments under the `common` namespace, not separate namespaces.
    i18n.addResourceBundle(lang, 'common', data, true, true);
  }
}

// The JSON is a flat map of path segments ({ common:{...}, nav:{...},
// landing:{...}, auth:{...}, profile:{...} }) treated as one big "common"
// namespace so `t('landing.hero_title_line1')` resolves through i18next's
// dot-navigation. Pass the WHOLE file — do not unwrap `.common`.
const EN_RESOURCE = enCommon;

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    fallbackLng: 'en',
    supportedLngs: SUPPORTED_LANGS.map((l) => l.code),
    load: 'languageOnly',
    defaultNS: 'common',
    ns: ['common'],
    interpolation: { escapeValue: false },
    react: { useSuspense: false },
    detection: {
      // We prefer an explicit choice saved in localStorage over the browser locale.
      order: ['localStorage', 'navigator', 'htmlTag'],
      lookupLocalStorage: 'becaim_biz_lang',
      caches: ['localStorage'],
    },
    // Ship EN in the initial bundle so the first render has real strings,
    // not raw dotted keys. Other langs load on demand.
    resources: { en: { common: EN_RESOURCE } },
  });

// If the user's saved/detected language isn't English, fetch it BEFORE the
// first render (index.js awaits `ready`). Module-level copy (tab labels,
// status maps, nav items) is resolved through `i18n.t` when a page module
// loads, so the bundle has to be in place before any page module evaluates.
export const ready = (async () => {
  const active = (i18n.language || 'en').split('-')[0];
  if (active !== 'en') await preload(active);
})();

// Public helper so the language selector can trigger fresh downloads.
// A language change reloads the app: every screen — including copy that was
// resolved when its module loaded — comes back consistently in the new
// language, and the backend's `X-User-Lang` header switches for AI content.
export async function setLanguage(code, { reload = true } = {}) {
  if (!SUPPORTED_LANGS.find((l) => l.code === code)) return;
  await preload(code);
  await i18n.changeLanguage(code);
  try { localStorage.setItem('becaim_biz_lang', code); } catch (_) { /* ignore */ }
  if (reload && typeof window !== 'undefined') window.location.reload();
}

export default i18n;
